// Has:
// 1) Task progress stepper
// 2) Siren Categories control
// 3) Channels control

import { FC, useMemo } from 'react';
import {
  AudioAnnotationTypes,
  NUM_ANNOTATION_TYPES,
} from 'packages/main/components/audio-tool/audio-tool-types';
import { useRecoilCallback, useRecoilValue } from 'recoil';
import {
  curAnnotationTypesAtom,
  curChannelsAtom,
  userCanEditAtom,
} from 'packages/main/components/audio-tool/atoms';
import { useUserProfile } from 'packages/lib/utils/hooks/use-auth-data';
import { css } from '@emotion/css';
import { countNumRegionsTotal } from 'packages/main/components/audio-tool/utils';
import { Modal, Select, Steps, Tooltip } from 'antd';
import { assignTaskToUser } from 'packages/lib/utils/legacy-tsapi/use-task-group-tasks';
import { SaveStateIndicator } from 'packages/main/components/audio-tool/save-state-indicator';
import CenteredBox from 'packages/lib/common-ui/centeredBox';
import { FlagOutlined } from '@ant-design/icons';
import { KeyedMutator } from 'swr';
import type { Task } from 'packages/lib/api/annotations/contract/task';
import { taskStateZod } from 'packages/lib/api/annotations/contract/enums';

const styles: Record<string, React.CSSProperties> = {
  categorySelect: { minWidth: '400px', margin: 0, padding: 0 },
  channelSelect: { minWidth: '600px', margin: 0, padding: 0 },
  header: {
    backgroundColor: 'rgba(255,255,255, 0)',
    minHeight: '100px',
    maxHeight: '140px',
    paddingTop: '10px',
  },
  headerProgressOuterDiv: {
    display: 'grid',
    gridTemplateColumns: '0.2fr 1fr 0.2fr',
  },
  headerProgress: {
    display: 'flex',
    justifyContent: 'center',
  },
  headerCategoryAndChannelControls: {
    display: 'grid',
    gridTemplateColumns: '1fr 1.35fr',
    justifyItems: 'stretch',
    justifyContent: 'space-between',
    minWidth: '1300px',
    paddingTop: '10px',
  },
};

// The standard Ant 4.x stepper control's light gray line is the EXACT same color as the default light gray background on our site,
// and won't show up. We make it a darker gray ('lightgray') so it's visible.
const stepperClass = css`
  .ant-steps-item-wait .ant-steps-item-title:after,
  .ant-steps-item-process .ant-steps-item-title:after {
    background-color: lightgray !important;
  }
`;

const stepsProgressItems = [
  {
    title: 'In Progress',
  },
  {
    title: 'In Review',
  },
];

const stepsDoneStateItem = [
  {
    title: 'Done',
  },
];

const TASK_STATE_TO_STEPS = {
  [taskStateZod.enum.ToDo]: 0,
  [taskStateZod.enum.InProgress]: 0,
  [taskStateZod.enum.InReview]: 1,
  [taskStateZod.enum.Done]: 2,
  [taskStateZod.enum.Rejected]: 2,
};
function taskStateToStepsIndex(task: Task): number {
  const stepsIdx: number = TASK_STATE_TO_STEPS[task.state];
  if (stepsIdx) return stepsIdx;
  return 0;
}

const redFlagIconOnCircle = (
  <div
    style={{
      borderRadius: '50%',
      backgroundColor: '#EBB8B7',
      width: '25px',
      height: '25px',
    }}
  >
    <CenteredBox>
      <FlagOutlined
        style={{
          color: 'red',
          fontSize: '12px',
        }}
      />
    </CenteredBox>
  </div>
);

const annotationTypeMenuOptions = [
  { label: 'Wail', value: AudioAnnotationTypes.Wail },
  { label: 'Yelp', value: AudioAnnotationTypes.Yelp },
  { label: 'Piercer', value: AudioAnnotationTypes.Piercer },
  { label: 'Horn', value: AudioAnnotationTypes.Horn },
  { label: 'Other', value: AudioAnnotationTypes.Other },
];

const channelOptions = [
  { label: '0: Rear Passenger', value: '0' },
  { label: '1: Rear Driver', value: '1' },
  { label: '2: Front Driver', value: '2' },
  { label: '3: Front Passenger', value: '3' },
];

interface AudioToolHeaderControlsProps {
  task: Task | null;
  reloadTask: KeyedMutator<Task[]>;
}

export const AudioToolHeaderControls: FC<
  React.PropsWithChildren<AudioToolHeaderControlsProps>
> = ({ task, reloadTask, children }) => {
  const _curChannels = useRecoilValue(curChannelsAtom);
  const _userCanEdit = useRecoilValue(userCanEditAtom);
  const _curAnnotationTypes = useRecoilValue(curAnnotationTypesAtom);
  const { userProfile } = useUserProfile();

  // Ant wants a Select's Options to have string values, not number values
  const curChannelsInts = useMemo(
    () => Array.from(_curChannels).map(String),
    [_curChannels]
  );

  const onChangeVisibleAnnotationTypes = useRecoilCallback(
    ({ snapshot, set }) =>
      async (curAnnotationTypes: string[]): Promise<void> => {
        const prevTypes = new Set(
          await snapshot.getPromise(curAnnotationTypesAtom)
        );
        const newTypes = new Set(curAnnotationTypes);

        const removedItems: string[] = [];
        for (const item of prevTypes) {
          if (!newTypes.has(item)) removedItems.push(item);
        }

        // If the user has removed an annotation category, we want to check first if there are any annotations in that category.
        // If so, we prompt the user to delete those annotations before we allow them to remove the category.
        if (removedItems.length > 0) {
          const numRegions = await countNumRegionsTotal(snapshot, removedItems);
          const removedItemsStr = removedItems.join(', ');

          if (numRegions > 0) {
            Modal.warning({
              title: `${removedItemsStr} Annotations must be deleted first`,
              content: `The ${
                removedItems.length > 1
                  ? 'categories ' + removedItemsStr + ' contain'
                  : 'category ' + removedItemsStr + ' contains'
              } ${numRegions} annotations. These must be deleted before you can remove this annotations category.`,
            });
          } else {
            set(
              curAnnotationTypesAtom,
              curAnnotationTypes as Array<AudioAnnotationTypes>
            );
          }
        } else {
          set(
            curAnnotationTypesAtom,
            curAnnotationTypes as Array<AudioAnnotationTypes>
          );

          // Change Task state to InProgress and assign to the current user?
          if (task && task.state === taskStateZod.enum.ToDo && userProfile) {
            await assignTaskToUser(task.token, userProfile.email);
            await reloadTask();
          }
        }
      },
    [userProfile, task, reloadTask]
  );

  const onSelectChannel = useRecoilCallback(
    ({ snapshot, set }) =>
      async (value: string | number): Promise<void> => {
        set(curChannelsAtom, (curVal) => new Set(curVal).add(Number(value)));
      }
  );

  const onDeselectChannel = useRecoilCallback(
    ({ snapshot, set }) =>
      async (value: string | number): Promise<void> => {
        set(curChannelsAtom, (curVal) => {
          const newSet = new Set(curVal);
          newSet.delete(Number(value));
          return newSet;
        });
      }
  );

  const stepsRejectedStateItem = [
    {
      title: 'Rejected',
      icon: (
        <Tooltip
          title={task ? 'Reason: ' + task.comments : ''}
          placement="bottom"
        >
          {redFlagIconOnCircle}
        </Tooltip>
      ),
    },
  ];

  return (
    <>
      <div style={styles.headerProgressOuterDiv}>
        <div />
        <div style={styles.headerProgress}>
          {task && (
            <div style={{ width: '60%' }}>
              <Steps
                className={stepperClass}
                current={taskStateToStepsIndex(task)}
                size="small"
                items={[
                  ...stepsProgressItems,
                  ...(task.state === taskStateZod.enum.Rejected
                    ? stepsRejectedStateItem
                    : []),
                  ...(task.state !== taskStateZod.enum.Rejected
                    ? stepsDoneStateItem
                    : []),
                ]}
              />
            </div>
          )}
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'end',
            alignItems: 'center',
            height: '36px',
          }}
        >
          <SaveStateIndicator />
        </div>
      </div>
      <div style={styles.headerCategoryAndChannelControls}>
        <div
          style={{
            lineHeight: '4px',
          }}
        >
          <b>Siren Categories</b>
          {'  '}
          <Select
            mode="tags"
            allowClear
            maxTagCount={NUM_ANNOTATION_TYPES}
            value={_curAnnotationTypes}
            options={annotationTypeMenuOptions}
            onChange={onChangeVisibleAnnotationTypes}
            placeholder="Select all siren sound types present in the audio clip"
            style={styles.categorySelect}
            disabled={!_userCanEdit}
            onClick={(e): void => {
              e.stopPropagation();
            }}
          />
        </div>
        <div
          style={{
            lineHeight: '4px',
          }}
        >
          <b>Channels</b>
          {'  '}
          <Select
            mode="tags"
            maxTagCount={4}
            value={curChannelsInts}
            options={channelOptions}
            onSelect={onSelectChannel}
            onDeselect={onDeselectChannel}
            placeholder="Select channels to unmute them and see a waveform"
            style={styles.channelSelect}
            onClick={(e): void => {
              e.stopPropagation();
            }}
          />
        </div>
      </div>
    </>
  );
};
